import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'filled';
  isValid?: boolean;
  isLoading?: boolean;
  showSuccessIcon?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    type = 'text', 
    label, 
    error, 
    helperText, 
    variant = 'default', 
    id,
    isValid,
    isLoading,
    showSuccessIcon = true,
    leftIcon,
    rightIcon,
    required,
    ...props 
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const [isFocused, setIsFocused] = useState(false);
    
    const baseStyles = 'flex h-12 md:h-11 w-full rounded-xl border px-4 md:px-3 py-2.5 md:py-2 text-base md:text-base transition-all duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-secondary-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';
    
    const variants = {
      default: 'bg-white border-secondary-200 focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500/20',
      filled: 'bg-secondary-50 border-secondary-100 focus-visible:bg-white focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500/20'
    };
    
    const errorStyles = error 
      ? 'border-red-400 focus-visible:border-red-500 focus-visible:ring-red-500/20 bg-red-50/50' 
      : '';
    
    const validStyles = isValid && showSuccessIcon && !error
      ? 'border-green-400 focus-visible:border-green-500 focus-visible:ring-green-500/20'
      : '';

    const hasLeftIcon = leftIcon || (type === 'tel');
    const hasRightIcon = rightIcon || isLoading || (isValid && showSuccessIcon && !error) || error;

    return (
      <div className="space-y-1.5">
        {label && (
          <label 
            htmlFor={inputId}
            className={cn(
              "text-sm font-medium leading-none block transition-colors duration-200",
              isFocused ? "text-primary-600" : "text-secondary-700",
              error && "text-red-600"
            )}
          >
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        
        <div className="relative">
          {/* Left Icon (e.g., phone icon for tel type) */}
          {hasLeftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400">
              {leftIcon || (type === 'tel' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              ))}
            </div>
          )}

          <input
            type={type}
            id={inputId}
            className={cn(
              baseStyles,
              variants[variant],
              errorStyles,
              validStyles,
              hasLeftIcon && 'pl-11 md:pl-11',
              hasRightIcon && 'pr-11 md:pr-11',
              className
            )}
            ref={ref}
            required={required}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={{ fontSize: '16px' }} // Prevent iOS zoom
            {...props}
          />

          {/* Right Side Icons */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {/* Loading Spinner */}
            <AnimatePresence>
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="w-5 h-5 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"
                />
              )}
            </AnimatePresence>

            {/* Success Icon */}
            <AnimatePresence>
              {isValid && showSuccessIcon && !error && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className="text-green-500"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Icon */}
            <AnimatePresence>
              {error && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className="text-red-500"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Custom Right Icon */}
            {rightIcon && !isLoading && !isValid && !error && rightIcon}
          </div>
        </div>

        {/* Error Message with Animation */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.p 
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              transition={{ duration: 0.2 }}
              className="text-sm text-red-600 flex items-center gap-1.5"
            >
              <motion.span
                initial={{ rotate: -90 }}
                animate={{ rotate: 0 }}
                transition={{ duration: 0.3, type: 'spring' }}
              >
                <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </motion.span>
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Helper Text */}
        <AnimatePresence>
          {helperText && !error && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xs text-secondary-500"
            >
              {helperText}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
